// UI-CLAIM-ACTIVITY-2 (2026-05-18) — multi-source claim timeline.
//
// Reads GET /claims/{id}/activity which unions diary, tasks, payments,
// reserves, documents, policy attachments, and claim open/close events
// into one reverse-chronological feed.  Read-only.  Filter chips to
// scope by category.
//
// Privacy: respects existing ClaimDetail READ_CLAIM_FULL gating + writes
// a phi_access('activity') audit row.  Audit-log and PHI-access logs are
// intentionally NOT surfaced here (internal-only per privacy directive).
// Peer productivity/workload data is not exposed.

import { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../api";
import { Spinner, EmptyState } from "./ui";  // UI-SWEEP-CLAIM

// Filter chip → event_type set.
export const FILTERS = [
  { id: "all",       label: "All",          types: null },
  { id: "notes",     label: "Notes",        types: new Set(["note"]) },
  { id: "tasks",     label: "Tasks",        types: new Set(["task_created", "task_completed"]) },
  { id: "documents", label: "Documents",    types: new Set(["document"]) },
  { id: "emails",    label: "Emails",       types: new Set(["email"]) },
  { id: "financial", label: "Financial",    types: new Set(["payment", "reserve_change"]) },
  { id: "policy",    label: "Policy",       types: new Set(["policy_attached", "policy_detached"]) },
  { id: "claim",     label: "Claim Events", types: new Set(["claim_opened", "claim_closed"]) },
];

// Per-event-type styling.  Restrained palette per the UI memo §22.
export const TYPE_META = {
  note:            { label: "Note",      color: "var(--navy, #1F3864)" },
  task_created:    { label: "Task",      color: "#1565C0" },
  task_completed:  { label: "Task ✓",    color: "#2E7D32" },
  payment:         { label: "Payment",   color: "#6A1B9A" },
  reserve_change:  { label: "Reserve",   color: "#B07000" },
  document:        { label: "Document",  color: "#4A148C" },
  email:           { label: "Email",     color: "#2C6E8F" },
  policy_attached: { label: "Policy",    color: "#0B5C5C" },
  policy_detached: { label: "Policy",    color: "#888" },
  claim_opened:    { label: "Opened",    color: "#2E7D32" },
  claim_closed:    { label: "Closed",    color: "#666" },
};

export default function ActivityTab({ claimId }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    setErr(null);
    apiFetch(`/claims/${claimId}/activity`)
      .then(d => { setEvents(d || []); setLoading(false); })
      .catch(e => { setErr(String(e.message || e)); setLoading(false); });
  }, [claimId]);

  // Counts per chip — gray out empty buckets so the user knows what's there.
  const countsByFilter = useMemo(() => {
    const out = { all: events.length };
    for (const f of FILTERS) {
      if (!f.types) continue;
      out[f.id] = events.filter(e => f.types.has(e.event_type)).length;
    }
    return out;
  }, [events]);

  const filtered = useMemo(() => {
    const f = FILTERS.find(x => x.id === activeFilter);
    if (!f || !f.types) return events;
    return events.filter(e => f.types.has(e.event_type));
  }, [events, activeFilter]);

  return (
    <div>
      <div className="section-header" style={{ alignItems: "baseline" }}>
        <span className="section-title">
          Activity{!loading && events.length > 0 ? ` (${events.length})` : ""}
        </span>
        <span style={{ fontSize: 11, color: "var(--text3,#888)" }}>
          {!loading && !err && events.length > 0 && `${filtered.length} of ${events.length} shown`}
        </span>
      </div>

      {/* Filter chips */}
      {!loading && !err && events.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {/* CLAIMS-WORKSPACE-FINISH-1: hide zero-count buckets (keep All + the
              active filter) so the chip row only shows what's actually there. */}
          {FILTERS.filter(f => !f.types || (countsByFilter[f.id] ?? 0) > 0 || activeFilter === f.id).map(f => {
            const count = countsByFilter[f.id] ?? 0;
            const isActive = activeFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                style={{
                  padding: "5px 12px",
                  fontSize: 12,
                  border: `1px solid ${isActive ? "var(--navy,#1F3864)" : "var(--border,#DDE2E8)"}`,
                  background: isActive ? "var(--navy,#1F3864)" : "white",
                  color: isActive ? "white" : "var(--text,#333)",
                  borderRadius: 14,
                  cursor: "pointer",
                  fontWeight: isActive ? 600 : 400,
                }}>
                {f.label}
                <span style={{ marginLeft: 4, fontSize: 11, opacity: 0.7 }}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="card">
        {loading ? (
          <Spinner label="Loading activity…" />
        ) : err ? (
          <div className="alert-error" style={{ margin: 14 }}>{err}</div>
        ) : events.length === 0 ? (
          <EmptyState icon="🕘" title="No activity yet" hint="Notes, payments, reserve changes, and claim events will appear here as they happen." />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            No {FILTERS.find(f => f.id === activeFilter)?.label.toLowerCase()} events.
          </div>
        ) : (
          <div style={{ padding: "4px 0" }}>
            {(() => {
              // Date-group the (already desc-sorted) feed with sticky-ish headers.
              const out = [];
              let lastBucket = null;
              filtered.forEach((e, idx) => {
                const b = bucketOf(e.occurred_at);
                if (b !== lastBucket) {
                  out.push(
                    <div key={`h-${idx}`} style={{ padding: "8px 16px 4px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--text3,#888)", background: "var(--bg2,#fafbfc)", borderBottom: "1px solid var(--border-light,#EEF1F4)" }}>{b}</div>
                  );
                  lastBucket = b;
                }
                out.push(<EventRow key={idx} ev={e} />);
              });
              return out;
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function EventRow({ ev }) {
  const meta = TYPE_META[ev.event_type] || { label: ev.event_type, color: "#888" };
  // LD-DESIGN-012 pattern 6: date-gutter timeline — date rail on the left,
  // hairline, then the type chip + content.
  const dt = ev.occurred_at ? new Date(ev.occurred_at) : null;
  // Emails carry their full (signature-stripped) body + attachment list; give
  // them a larger preview and an expand toggle instead of a hard cut.
  const [expanded, setExpanded] = useState(false);
  const isEmail = ev.event_type === "email";
  const limit = isEmail ? 600 : 280;
  const attachments = isEmail && Array.isArray(ev.meta?.attachments) ? ev.meta.attachments : [];
  const openDoc = async (a) => {
    try {
      const res = await apiFetch(`/documents/${a.id}/download`, { raw: true });
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { /* PHI-walled or deleted doc → silently no-op */ }
  };
  return (
    <div style={{
      padding: "10px 16px",
      borderBottom: "1px solid var(--border-light,#EEF1F4)",
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
    }}>
      <div style={{ flexShrink: 0, width: 54, textAlign: "right", paddingTop: 2 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--navy,#1F3864)", whiteSpace: "nowrap" }}>
          {dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
        </div>
        <div style={{ fontSize: 10, color: "var(--text3,#888)", whiteSpace: "nowrap" }}>
          {dt ? dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : ""}
        </div>
      </div>
      <div style={{ width: 2, alignSelf: "stretch", background: meta.color, borderRadius: 1, flexShrink: 0, opacity: 0.55 }} />
      {/* Type badge — narrow strip on the left */}
      <span style={{
        display: "inline-block",
        flexShrink: 0,
        background: meta.color,
        color: "white",
        fontSize: 10,
        padding: "3px 8px",
        borderRadius: 3,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: 600,
        minWidth: 72,
        textAlign: "center",
      }}>{meta.label}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text,#1A1A1A)", marginBottom: 3 }}>
          {ev.title || "(no title)"}
        </div>
        {ev.body && (
          <div style={{ fontSize: 13, color: "var(--text2,#555)", marginBottom: 4, whiteSpace: "pre-wrap" }}>
            {expanded ? ev.body : truncate(ev.body, limit)}
            {ev.body.length > limit && (
              <button onClick={() => setExpanded(x => !x)}
                      style={{ background: "none", border: "none", color: "var(--blue,#2E75B6)", cursor: "pointer", padding: 0, marginLeft: 6, fontSize: 12, fontWeight: 600 }}>
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}
        {attachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
            {attachments.map(a => (
              <button key={a.id} onClick={() => openDoc(a)} title="Open attachment"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, background: "var(--bg3,#f8fafc)", border: "1px solid var(--border,#DDE2E8)", borderRadius: 12, padding: "2px 10px", cursor: "pointer", color: "var(--blue,#2E75B6)", fontWeight: 600 }}>
                📎 {a.filename || "attachment"}
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: 11, color: "var(--text3,#888)" }}>
          {ev.actor_name ? <span style={{ color: "var(--text2,#555)" }}>{ev.actor_name}</span> : ""}
          {ev.event_type === "note" && ev.meta?.is_internal &&
            <> · <span style={{ color: "#B07000" }}>internal</span></>}
        </div>
      </div>
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

// Time bucket for date-grouped feed headers.
function bucketOf(d) {
  if (!d) return "Earlier";
  const dt = new Date(d), now = new Date();
  const startToday = new Date(now.toDateString());
  if (dt >= startToday) return "Today";
  const yest = new Date(startToday); yest.setDate(yest.getDate() - 1);
  if (dt >= yest) return "Yesterday";
  const startWeek = new Date(startToday); startWeek.setDate(startWeek.getDate() - startToday.getDay());
  if (dt >= startWeek) return "This Week";
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  if (dt >= startMonth) return "This Month";
  return dt.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
